import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  ApplicationData,
  createCheckoutSession,
  downloadBcComplianceDoc,
  getApplicationById,
  getPackgageById,
  submitApplication,
} from "@/api/backgroundCheck";
import { Check, ChevronRight, NotebookText, TriangleAlert } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useSelector } from "react-redux";
import { AppDispatch, AppState } from "@/store";
import { useDispatch } from "react-redux";
import { getPackagesAction, Package, setSelectedPackage } from "@/store/slices/packageSlice";
import { Loading } from "@/components/ui/loading";
import { formatDate } from "@/utils/datetime";
import { useMixpanel } from "@/hooks/useMixpanel";
import { useTranslation } from "react-i18next";
import { PhoneInput } from "@/components/ui/phoneInput";

const formSchema = z
  .object({
    first_name: z.string().min(1, { message: "First name is required" }),
    middle_name: z.string().optional(),
    last_name: z.string().min(1, { message: "Last name is required" }),
    social_security_no: z
      .string()
      .regex(/^\d{3}-\d{2}-\d{4}$/, { message: "Invalid SSN format (XXX-XX-XXXX)" })
      .min(11, { message: "Social Security Number is required" }),
    dob: z.date().min(new Date(1900, 0, 1), { message: "Invalid date of birth" }),
    email: z.string().email({ message: "Invalid email address" }),
    address: z.object({
      country_code: z.string().min(1, { message: "Country is required" }),
      country_name: z.string(),
      state_code: z.string().min(1, { message: "State is required" }),
      state_name: z.string(),
      city: z.string().min(1, { message: "City is required" }),
      address1: z.string().min(1, { message: "Address is required" }),
      from_date: z.date().min(new Date(1900, 0, 1), { message: "From date is required" }),
      postal_code: z
        .string()
        .min(5, { message: "Postal code is required" })
        .regex(/^\d{5}$/, { message: "Postal code must be exactly 5 digits" }),
    }),
    license_info: z.object({
      license_number: z.string().min(1, { message: "License number is required" }),
      country_code: z.string().min(1, { message: "Country is required" }),
      state_code: z.string().min(1, { message: "State is required" }),
    }),
    phone: z.string().min(10, { message: "Invalid phone number" }),
    lived_in_ny: z.enum(["Yes", "No"], { message: "This field is required" }),
    check1: z
      .boolean()
      .refine((val) => val, { message: "You must agree to the terms and conditions" }),
    check2: z
      .boolean()
      .refine((val) => val, { message: "You must agree to the compliance document" }),
  })
  .optional();

type FormData = z.infer<typeof formSchema>;

const countries = [{ name: "United States", countryCode: "US" }];
export default function JobSeekerVerificationApplicationDetail() {
  const { id } = useParams();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { trackEvent } = useMixpanel();
  const [searchParams] = useSearchParams();
  const notificationId = searchParams.get("notification-id");
  const chatId = searchParams.get("chatId");
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState(true);
  const { allPackages, selectedPackage } = useSelector((state: AppState) => state.packages);
  const { user } = useSelector((state: AppState) => state.auth.userData);
  const [showProceedModal, setShowProceedModal] = useState(false);
  const [packageDetails, setPackageDetails] = useState<any>(null);
  const [jobSeekerFullName, setJobSeekerFullName] = useState<string>(null);
  const [status, setStatus] = useState<string>("");
  const [procced, setProcced] = useState<Package>(null);
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      address: {
        country_code: "",
        state_code: "",
        city: "",
        address1: "",
        postal_code: "",
      },
      license_info: {
        license_number: "",
        country_code: "",
        state_code: "",
      },
    },
  });

  const fetchPackageDetails = async (packageId: string) => {
    try {
      const response = await getPackgageById(packageId!);
      setPackageDetails(response.result);
      dispatch(setSelectedPackage(response.result));
    } catch (error) {
      toast({ title: "Error fetching package details", description: error.message });
    }
  };
  useEffect(() => {
    if (!allPackages.length) {
      dispatch(getPackagesAction("employer"));
      trackEvent("Employer - Bc application review page");
    }
  }, [dispatch, allPackages.length]);
  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await getApplicationById(id!);
      const data = result.result;
      setStatus(data.status);
      setJobSeekerFullName(data.jobseeker.full_name);
      fetchPackageDetails(data.package.id);
      form.setValue("first_name", data.first_name);
      form.setValue("middle_name", data.middle_name);
      form.setValue("last_name", data.last_name);
      form.setValue("email", data.email);
      form.setValue("phone", data.phone.slice(6));
      form.setValue("dob", data.dob);
      form.setValue("social_security_no", data.social_security_no);
      form.setValue("address", {
        address1: data.address.address1,
        from_date: data.address.from_date,
        city: data.address.city,
        state_code: data.address.state_name,
        country_code: data.address.country_name,
        postal_code: data.address.postal_code,
      });
      form.setValue("license_info", {
        license_number: data.license_info.license_number,
        country_code: data.license_info.country_name,
        state_code: data.license_info.state_name,
      });
      form.setValue("lived_in_ny", data?.ny_county ? "Yes" : "No");
      form.setValue("check1", true);
      form.setValue("check2", true);
    } catch (error: any) {
      toast({ title: "Error", description: error.message });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    if (chatId) {
      dispatch(getPackagesAction("employer"));
    }
  }, [chatId]);

  const handleProceedWithNY = async () => {
    const nyPkgSuffix = selectedPackage.pkg_id.endsWith("_js") ? "_ny_js" : "_ny";

    const nyPkgId = selectedPackage.pkg_id.replace(/(_js)?$/, nyPkgSuffix);

    const nyPackage = allPackages.find((pkg) => pkg.pkg_id === nyPkgId);

    if (nyPackage) {
      dispatch(setSelectedPackage(nyPackage));
      const checkoutSession = await createCheckoutSession({
        pkg_id: nyPackage.pkg_id,
        bc_application_id: id,
        success_url: notificationId
          ? `${window.location.origin}/employer/verification-checks/dashboard`
          : `${window.location.origin}/employer/chats/${chatId}`,
      });
      window.location.href = checkoutSession.result.url;
      trackEvent("Submit Background Check Application", { id, package: nyPackage });
    } else {
      toast({ title: "Error", description: "New York package not found!" });
      return;
    }

    setShowProceedModal(false);
  };

  const onSubmit = async () => {
    const data = form.getValues();
    const isNY = data.lived_in_ny === "Yes";
    // const isNYPackageSuffix = bcApplicationId ? "_ny" : "_ny_js";
    const isNYPackage = packageDetails?.is_ny;
    if (isNY && !isNYPackage) {
      const nyPkgId = `${selectedPackage.pkg_id}_ny`;

      const nyPackage = allPackages.find((pkg) => pkg.pkg_id === nyPkgId);
      if (nyPackage) {
        setProcced(nyPackage);
        setShowProceedModal(true);
        return;
      }
    } else {
      const checkoutSession = await createCheckoutSession({
        bc_application_id: id,
        success_url: notificationId
          ? `${window.location.origin}/employer/verification-checks/dashboard`
          : `${window.location.origin}/employer/chats/${chatId}`,
      });
      window.location.href = checkoutSession.result.url;
    }
  };
  if (!packageDetails) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <>
      {chatId && (
        <div className="mb-4 flex flex-wrap gap-4 font-semibold text-muted-foreground">
          <div
            onClick={() => {
              navigate("/employer/chats");
              trackEvent("Navigate to Chats");
            }}
            style={{ cursor: "pointer" }}
          >
            {t("sidebar.jobseeker.chats")}
          </div>
          <ChevronRight />
          <div
            className="cursor-pointer"
            onClick={() => {
              navigate(`/employer/chats/${chatId}`);
              trackEvent("Navigate to Chat Details", { chatId });
            }}
          >
            {jobSeekerFullName}
          </div>
          <ChevronRight />
          <div>{`${packageDetails?.name} - ${t("background_check_application")}`}</div>
        </div>
      )}
      <div className="md:p-6 ">
        <h1 className="mb-6 text-xl font-semibold">{t("background_check_application")}</h1>
        <p className="text-lg font-semibold text-secondary">
          {packageDetails?.name} - ${packageDetails?.price}
        </p>

        <div className="mx-auto my-8 max-w-3xl">
          <p className="mb-6 text-muted-foreground">
            {t("jobseeker_bc_application_form.instruction.review_documents")}
          </p>
          <p
            className="mb-5 flex cursor-pointer items-center gap-1 font-semibold text-primary"
            onClick={() => {
              downloadBcComplianceDoc(user.organization.id, user.full_name, "employer");
              trackEvent("Open Compliance Document");
            }}
          >
            <NotebookText size={16} />
            {t("jobseeker_bc_application_form.label.compliance")}
          </p>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex w-full flex-col gap-10 md:flex-row"
            >
              <div className="flex flex-col gap-6 md:w-1/2">
                <FormField
                  control={form.control}
                  name="first_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("jobseeker_bc_application_form.label.first_name")}</FormLabel>
                      <Input {...field} readOnly />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="middle_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={false}>
                        {t("jobseeker_bc_application_form.label.middle_name")}
                      </FormLabel>
                      <Input placeholder="Enter your middle name" {...field} readOnly />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="last_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("jobseeker_bc_application_form.label.last_name")}</FormLabel>
                      <Input {...field} readOnly />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="social_security_no"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("jobseeker_bc_application_form.label.social_security_no")}
                      </FormLabel>
                      <Input
                        type="text"
                        value={field.value}
                        maxLength={11}
                        onChange={(e) => {
                          let input = e.target.value;
                          input = input.replace(/\D/g, "");
                          if (input.length > 5) {
                            input = `${input.slice(0, 3)}-${input.slice(3, 5)}-${input.slice(5)}`;
                          } else if (input.length > 3) {
                            input = `${input.slice(0, 3)}-${input.slice(3)}`;
                          }
                          field.onChange(input);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Backspace") {
                            const unformatted = field.value.replace(/-/g, "");
                            field.onChange(unformatted);
                          }
                        }}
                        readOnly
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="dob"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("jobseeker_bc_application_form.label.dob")}</FormLabel>
                      <Input {...field} readOnly value={formatDate(field.value)} />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("jobseeker_bc_application_form.label.email")}</FormLabel>
                      <Input type="email" {...field} readOnly />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address.address1"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("jobseeker_bc_application_form.label.address")}</FormLabel>
                      <Input {...field} readOnly />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address.from_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("from")}</FormLabel>
                      <div className="flex items-center gap-2">
                        <Input {...field} readOnly value={formatDate(field.value)} />{" "}
                        <p className="whitespace-nowrap font-medium italic">- {t("current")}</p>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address.postal_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("jobseeker_bc_application_form.label.zip_code")}</FormLabel>
                      <Input
                        type="text"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value.replace(/[^0-9]/g, ""))}
                        readOnly
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address.country_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("jobseeker_bc_application_form.label.country")}</FormLabel>
                      <Input {...field} readOnly />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex flex-col gap-6 md:w-1/2">
                <FormField
                  control={form.control}
                  name="address.state_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("jobseeker_bc_application_form.label.state")}</FormLabel>
                      <Input {...field} readOnly />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address.city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("jobseeker_bc_application_form.label.city")}</FormLabel>
                      <Input {...field} readOnly />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("jobseeker_bc_application_form.label.contact_number")}
                      </FormLabel>
                      <PhoneInput
                        placeholder={t("jobseeker_bc_application_form.placeholder.contact_number")}
                        {...field}
                        value={field.value}
                        defaultCountry="US"
                        readOnly
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <p className="my-5 border-b-2 border-input pb-3 text-lg font-semibold text-primary">
                  {t("jobseeker_bc_application_form.label.license_info")}
                </p>
                <FormField
                  control={form.control}
                  name="license_info.country_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("jobseeker_bc_application_form.label.license_country")}
                      </FormLabel>
                      <Input {...field} readOnly />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="license_info.state_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("jobseeker_bc_application_form.label.license_state")}
                      </FormLabel>
                      <Input {...field} readOnly />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="license_info.license_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("jobseeker_bc_application_form.label.license_number")}
                      </FormLabel>
                      <Input {...field} readOnly />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lived_in_ny"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="md:whitespace-nowrap">
                        {t("jobseeker_bc_application_form.label.lived_in_ny")}
                      </FormLabel>
                      <div className="w-3/5">
                        <Input {...field} readOnly />
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="check1"
                  render={({ field }) => (
                    <FormItem>
                      <div className="relative flex flex-row-reverse items-start justify-start rounded-md md:mt-8">
                        <FormLabel className="-mt-1 pl-3 text-sm text-muted-foreground">
                          {t("jobseeker_bc_application_form.label.check1")}
                        </FormLabel>
                        <FormControl>
                          <Checkbox
                            disabled
                            checked={field.value}
                            onCheckedChange={(checked) => {
                              field.onChange(checked);
                              // trackEvent("Signup Employer: Trial Checkbox Clicked", {
                              //   page: "Signup",
                              //   type: "employer",
                              // });
                            }}
                            className="m-0"
                          />
                        </FormControl>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="check2"
                  render={({ field }) => (
                    <FormItem>
                      <div className="relative flex flex-row-reverse items-start justify-start rounded-md ">
                        <FormLabel className="-mt-1 pl-3 text-sm text-muted-foreground">
                          {t("jobseeker_bc_application_form.label.check2")}
                        </FormLabel>
                        <FormControl>
                          <Checkbox
                            disabled
                            checked={field.value}
                            onCheckedChange={(checked) => {
                              field.onChange(checked);
                            }}
                            className="m-0"
                          />
                        </FormControl>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="button" onClick={onSubmit} className="w-full " disabled>
                  {loading ? "Submitting..." : t("Apply")}
                </Button>
              </div>
            </form>
          </Form>
          <div className="flex items-center justify-end"></div>
        </div>
        {/* {showProceedModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="w-96 rounded-lg bg-white p-6 shadow-lg">
              <h2 className="text-xl font-bold">
                {t("jobseeker_bc_application_form.proceed_modal.title")}
              </h2>
              <p className="mt-4 text-muted-foreground">
                {t("jobseeker_bc_application_form.proceed_modal.description")}
              </p>
              <ul className="my-4 space-y-2 font-semibold text-muted-foreground">
                <li className="flex gap-2">
                  <Check /> {procced?.name}
                </li>
                <li className="flex gap-2">
                  <Check />{" "}
                  {t("jobseeker_bc_application_form.proceed_modal.price", {
                    price: procced?.price,
                  })}
                </li>
              </ul>
              <p className="text-sm text-muted-foreground/50">
                {t("jobseeker_bc_application_form.proceed_modal.note")}
              </p>
              <Button variant="default" className="mt-6 w-full" onClick={handleProceedWithNY}>
                {t("proceed")}
              </Button>
            </div>
          </div>
        )} */}
      </div>
    </>
  );
}
